import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiLock, FiCpu, FiShield, FiArrowRight, FiAlertCircle } from 'react-icons/fi';

export const PasswordProtect = ({ onVerify }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(false);

    // Simulate network delay for "futuristic" feel
    setTimeout(() => {
      if (password === 'ineglobal') {
        onVerify();
      } else {
        setError(true);
        setIsSubmitting(false);
        // Shake animation trigger
      }
    }, 800);
  };

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-6 relative overflow-hidden selection:bg-blue-500/30">
      {/* Dynamic Background Elements */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-blue-600/10 rounded-full blur-[120px] -z-10"></div>
      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10 pointer-events-none"></div>
      
      {/* Animated Grid lines */}
      <div className="absolute inset-0 z-0 opacity-20" 
           style={{ 
             backgroundImage: `linear-gradient(#1e293b 1px, transparent 1px), linear-gradient(90deg, #1e293b 1px, transparent 1px)`,
             backgroundSize: '40px 40px' 
           }}>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        <div className="text-center mb-10">
          <motion.div 
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', damping: 12, stiffness: 200 }}
            className="w-20 h-20 bg-blue-600 rounded-3xl mx-auto flex items-center justify-center shadow-[0_0_40px_rgba(37,99,235,0.4)] mb-6 rotate-12"
          >
            <FiCpu className="text-white text-4xl" />
          </motion.div>
          
          <h1 className="text-3xl md:text-4xl font-black text-white mb-3 tracking-tighter leading-tight">
            THE FUTURE OF <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-500">
              FIBER OPTICS
            </span>
          </h1>
          <p className="text-slate-500 font-medium tracking-widest text-xs uppercase">
            Proprietary Field Support System
          </p>
        </div>

        <div className="glass-panel p-8 rounded-[32px] border-white/10 shadow-2xl relative overflow-hidden group">
          {/* Subtle glow effect */}
          <div className="absolute -top-24 -right-24 w-48 h-48 bg-blue-600/10 rounded-full blur-3xl group-hover:bg-blue-600/20 transition-all duration-700"></div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="text-slate-400 text-xs font-bold uppercase tracking-widest mb-3 block px-1">
                Security Access Key
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FiLock className={`transition-colors duration-300 ${error ? 'text-rose-500' : 'text-slate-500 group-focus-within:text-blue-400'}`} />
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter Access Code"
                  className={`w-full bg-slate-900/50 border-2 rounded-2xl py-4 pl-12 pr-4 text-white placeholder:text-slate-600 focus:outline-none transition-all duration-300 ${
                    error 
                    ? 'border-rose-500/50 focus:border-rose-500 shadow-[0_0_15px_rgba(244,63,94,0.1)]' 
                    : 'border-white/5 focus:border-blue-500/50 focus:ring-4 focus:ring-blue-500/10'
                  }`}
                />
              </div>
              
              <AnimatePresence>
                {error && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="flex items-center gap-2 text-rose-400 text-xs font-bold mt-3 px-1"
                  >
                    <FiAlertCircle /> Invalid Security Key. Access Denied.
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <button
              type="submit"
              disabled={isSubmitting || !password}
              className="w-full bg-white text-slate-950 font-black py-4 rounded-2xl flex items-center justify-center gap-2 hover:bg-blue-50 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed shadow-xl shadow-white/5 group"
            >
              {isSubmitting ? (
                <div className="w-5 h-5 border-2 border-slate-950/20 border-t-slate-950 rounded-full animate-spin"></div>
              ) : (
                <>
                  INITIALIZE SYSTEM <FiArrowRight className="group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>
        </div>

        <div className="mt-12 flex items-center justify-center gap-8 opacity-40 grayscale hover:grayscale-0 transition-all duration-500">
          <div className="flex items-center gap-2 text-slate-400 font-bold text-[10px] tracking-widest uppercase">
            <FiShield /> Encrypted
          </div>
          <div className="w-1 h-1 bg-slate-700 rounded-full"></div>
          <div className="flex items-center gap-2 text-slate-400 font-bold text-[10px] tracking-widest uppercase">
            <FiCpu /> Neural Core
          </div>
        </div>
      </motion.div>

      {/* Footer copyright */}
      <div className="absolute bottom-8 text-slate-600 font-bold text-[10px] tracking-[0.2em] uppercase">
        © 2026 AdamAI Security Protocol 4.0.2
      </div>
    </div>
  );
};